/**
 * The player's body: boxes textured from a procedural skin.
 *
 * Only visible in third person for now, but the same model will serve other
 * players and cossack NPCs later, so it takes a SkinDef rather than being
 * hard-wired to the local player.
 */

import * as THREE from "three";
import {
  paintSkin, layoutSkin, faceUV, SKIN_TEX, MODEL_PX_HEIGHT, overlayOf,
  type SkinDef, type BoxFaces, type Rect, type BoxSize, type PartName,
} from "../core/player/skins.js";
import { PLAYER_HEIGHT, CROUCH_HEIGHT } from "../core/player/controller.js";
import { makeHeldMesh, disposeHeldMesh } from "./heldItem.js";
import { heldModelGeneration, hasHeldModel } from "./heldModels.js";
import {
  skinModel, skinModelGeneration, hasSkinModel, type SkinModelInstance,
} from "./skinModels.js";
import type { Tone } from "../core/text/tone.js";
import { strikeAngle, STRIKE_REACH } from "../core/player/strike.js";
import { FIRST_NON_BLOCK_ITEM, heldStyle, drawnFlat } from "../core/block/items.js";
import type { Hand } from "../core/player/hand.js";

/** Half a turn about a held drawing's diagonal: a curved blade's edge turned the other way. */
const BLADE_FLIP = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 1, 0).normalize(), Math.PI);

/** Blocks per skin pixel, so every character is exactly PLAYER_HEIGHT tall. */
const PIXEL = PLAYER_HEIGHT / MODEL_PX_HEIGHT;

/**
 * One texture and one material per skin, not per body wearing it.
 *
 * Every orc in the game has the same skin. Painting it into its own canvas
 * for each one meant a hundred orcs held a hundred identical 128x128 textures
 * - six and a half megabytes of the same picture - and a raid every half hour
 * kept adding to the count. On a machine whose graphics memory is the system
 * memory, that is how a game takes the whole screen down with it.
 *
 * Keyed by skin id. Never disposed: a skin lives as long as the session, and
 * the alternative is reference counting for a handful of small textures.
 */
const skinCache = new Map<string, {
  texture: THREE.Texture;
  material: THREE.MeshBasicMaterial;
  /** The same texture again, for the layer that has holes in it. */
  outer: THREE.MeshBasicMaterial;
}>();

function skinMaterial(def: SkinDef): THREE.MeshBasicMaterial {
  const found = skinCache.get(def.id);
  if (found !== undefined) return found.material;

  // Skin pixels come from core; turning them into a texture is this layer's
  // only job. ImageData wants exactly the RGBA buffer paintSkin produces.
  const canvas = document.createElement("canvas");
  canvas.width = SKIN_TEX;
  canvas.height = SKIN_TEX;
  const ctx = canvas.getContext("2d");
  if (ctx === null) throw new Error("2D canvas context unavailable");
  if (def.image === undefined) {
    const image = ctx.createImageData(SKIN_TEX, SKIN_TEX);
    image.data.set(paintSkin(def));
    ctx.putImageData(image, 0, 0);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.colorSpace = THREE.SRGBColorSpace;

  const material = new THREE.MeshBasicMaterial({
    map: texture,
    // Unlit, like the terrain: the day/night tint is applied as a colour
    // multiply so the character darkens with the world.
  });
  /*
   * And the same texture again for the outer layer, cut out rather than
   * blended.
   *
   * `alphaTest` throws away any pixel the painter did not touch, which is
   * what lets a sleeve end in rags and hair end in strands instead of in a
   * straight edge. Cut out and *not* `transparent`: a transparent material
   * is sorted against everything else in the scene every frame, and a body
   * made of six of them flickers through itself as it turns. A cut-out
   * writes depth like anything solid and cannot.
   *
   * `side: DoubleSide` because the inside of a hanging sleeve is a face you
   * see from behind once the near side of it has been cut away.
   */
  const outer = new THREE.MeshBasicMaterial({
    map: texture,
    alphaTest: 0.5,
    side: THREE.DoubleSide,
  });
  skinCache.set(def.id, { texture, material, outer });

  /*
   * A drawn skin arrives late, and the canvas is already on screen.
   *
   * The file is fetched, drawn into the same canvas at two-to-one, and the
   * texture told it has changed - so a body that was standing there in an
   * empty skin for a frame or two fills in, rather than the game waiting on
   * the network before it will show anybody. Nearest-neighbour, because
   * smoothing pixel art is how a skin turns to soup.
   *
   * A skin that does not arrive leaves the body blank rather than taking the
   * game down with it: a missing file is a missing picture, not a crash.
   */
  if (def.image !== undefined) {
    const picture = new Image();
    picture.onload = () => {
      ctx.imageSmoothingEnabled = false;
      ctx.clearRect(0, 0, SKIN_TEX, SKIN_TEX);
      ctx.drawImage(picture, 0, 0, SKIN_TEX, SKIN_TEX);
      mirrorEmptyLimbs(ctx);
      texture.needsUpdate = true;
    };
    picture.onerror = () => {
      console.warn("skin unavailable", def.image?.file);
    };
    /*
     * Relative for the game, absolute where somebody passes one.
     *
     * The game is served from the root and asks for `./skins/...`, which is
     * right for it and wrong for anything on another path - the check's own
     * stage lives under /scripts/ and was quietly asking for
     * /scripts/skins/..., getting a 404, and rendering a body with no
     * picture on it. A leading slash means "exactly this".
     */
    picture.src = def.image.file.startsWith("/")
      ? def.image.file
      : `./skins/${def.image.file}`;
  }
  return material;
}

/**
 * A left limb that was never properly drawn is the right one, turned round.
 *
 * Skins have been through two shapes. The old sheet had one arm and one leg
 * and the body wore each of them twice; the newer one has all four, and a
 * file made by converting an old one - or drawn by somebody who only did the
 * side facing them - leaves the left-hand rectangles empty. An empty
 * rectangle on the body's first layer does not read as "no leg": it reads as
 * a black one, because the body is drawn solid and what is behind it is
 * nothing.
 *
 * So an empty left limb is filled from its right-hand twin, flipped, which
 * is what wearing the same rectangle twice amounted to. Done to the sheet
 * once on load rather than in the shader: it is four rectangles, once.
 *
 * The bar is different for the two layers, and the difference is the point.
 * The мавка's file came back with her left leg *thirty-one per cent* drawn -
 * not blank, a scatter of leaf pixels over a rectangle that should be solid
 * - and a body with a leg you can see through is not something anybody drew
 * on purpose. The first layer is opaque by definition, so anything short of
 * solid there is an unfinished rectangle. The second layer is the opposite:
 * a fringe of leaves *is* mostly holes, and judging it by the same bar would
 * throw away every overlay in the game.
 */
const LAYER_DRAWN = 0.6;
const OVERLAY_DRAWN = 0.05;

function mirrorEmptyLimbs(ctx: CanvasRenderingContext2D): void {
  // In sheet pixels, at the two-to-one scale this canvas is drawn at.
  const pairs: readonly (readonly [number, number, number, number,
    number, number, number])[] = [
    // from x, from y, to x, to y, width, height, the bar it has to clear.
    // The destinations are `classicLayout`'s own: armL at 32,48 in file
    // pixels is 64,96 here. This read 32,96 - the left *leg* - so the arm
    // was copied over the leg and the leg's own line then wrote over that.
    // Nothing looked wrong, because the leg came out right by accident and
    // the arm was simply never mirrored at all.
    [80, 32, 64, 96, 32, 32, LAYER_DRAWN],    // right arm -> left arm
    [80, 64, 96, 96, 32, 32, OVERLAY_DRAWN],  // right sleeve -> left sleeve
    [0, 32, 32, 96, 32, 32, LAYER_DRAWN],     // right leg -> left leg
    [0, 64, 0, 96, 32, 32, OVERLAY_DRAWN],    // right trouser -> left trouser
  ];
  for (const [fx, fy, tx, ty, w, h, bar] of pairs) {
    const there = ctx.getImageData(tx, ty, w, h);
    let painted = 0;
    for (let i = 3; i < there.data.length; i += 4) {
      if (there.data[i]! > 127) painted++;
    }
    if (painted > w * h * bar) continue;
    const from = ctx.getImageData(fx, fy, w, h);
    const flipped = ctx.createImageData(w, h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const a = (y * w + x) * 4;
        const b = (y * w + (w - 1 - x)) * 4;
        for (let c = 0; c < 4; c++) flipped.data[a + c] = from.data[b + c]!;
      }
    }
    ctx.putImageData(flipped, tx, ty);
  }
}

/** The cut-out material for a skin's second layer. */
function outerMaterial(def: SkinDef): THREE.MeshBasicMaterial {
  skinMaterial(def);
  return skinCache.get(def.id)!.outer;
}

/**
 * Write skin UVs onto a BoxGeometry.
 *
 * Three.js orders box faces +X, -X, +Y, -Y, +Z, -Z, four vertices each, laid
 * out top-left, top-right, bottom-left, bottom-right in UV space. The model
 * faces -Z (matching a player yaw of 0), so -Z is the character's front and
 * +X is their right.
 *
 * V counts down from the top of the texture because textures upload with
 * flipY - the same convention as the block atlas.
 */
function applySkinUVs(geometry: THREE.BufferGeometry, faces: BoxFaces): void {
  const order: Rect[] = [
    faces.right, faces.left,
    faces.top, faces.bottom,
    faces.back, faces.front,
  ];
  const uv = geometry.getAttribute("uv") as THREE.BufferAttribute;

  order.forEach((r, faceIndex) => {
    const { u0, u1, vTop, vBottom } = faceUV(r);
    const base = faceIndex * 4;
    uv.setXY(base + 0, u0, vTop);
    uv.setXY(base + 1, u1, vTop);
    uv.setXY(base + 2, u0, vBottom);
    uv.setXY(base + 3, u1, vBottom);
  });
  uv.needsUpdate = true;
}

function makePart(size: BoxSize, faces: BoxFaces, material: THREE.Material): THREE.Mesh {
  const geo = new THREE.BoxGeometry(size.w * PIXEL, size.h * PIXEL, size.d * PIXEL);
  applySkinUVs(geo, faces);
  return new THREE.Mesh(geo, material);
}

/**
 * A limb that swings from a joint.
 *
 * The mesh hangs half its own height below the pivot, so rotating the pivot
 * swings the limb from the shoulder or hip rather than about its middle.
 */
function makeJoint(
  size: BoxSize, faces: BoxFaces, material: THREE.Material,
  x: number, jointY: number,
): THREE.Group {
  const pivot = new THREE.Group();
  pivot.position.set(x, jointY, 0);
  const mesh = makePart(size, faces, material);
  mesh.position.y = -(size.h * PIXEL) / 2;
  pivot.add(mesh);
  return pivot;
}

/**
 * How big a held block is against a body, as a fraction of a block.
 *
 * A drawn item is bigger - see the note on VIEW_BLOCK_SCALE. A cube fills its
 * whole square and a шабля is a line across one, so one number for both makes
 * the sword a toy.
 */
const HELD_SCALE = 0.42;

/**
 * And how big a drawn item is, before what the item itself asks for.
 *
 * Its own number rather than a multiple of the block one. It used to be
 * `HELD_SCALE * 1.7`, from before items could ask for a size of their own,
 * and the two piled up: a шабля came out at one and a half blocks - a козак
 * is one point eight - so he was carrying something nearly his own height.
 */
const HELD_ITEM_SCALE = 0.55;

/**
 * What a body goes towards when it is struck.
 *
 * Bright rather than dark: the world tint already darkens everything at
 * night, and a hit that darkens further would be invisible exactly when a
 * fight is most confusing.
 */
const HURT_COLOUR = new THREE.Color(1, 0.28, 0.22);

export class PlayerModel {
  readonly group = new THREE.Group();

  private readonly material: THREE.MeshBasicMaterial;
  private readonly outer: THREE.MeshBasicMaterial;
  private readonly armR: THREE.Group;
  private readonly armL: THREE.Group;
  private readonly legR: THREE.Group;
  private readonly legL: THREE.Group;
  private readonly neck: THREE.Group;
  /** Torso, arms, head and robe: what a crouch lowers. */
  private above: THREE.Object3D[] = [];
  /** Where each of those sits when she is standing up. */
  private standing: number[] = [];
  private crouched = false;
  /** At the end of the weapon arm, so what is held swings with it. */
  private readonly hand = new THREE.Group();
  private readonly offHand = new THREE.Group();
  /** Which arm the weapon is on. See `setHand`. */
  private handedness: Hand = "right";
  /** How far in front of the arm's middle a shield has to sit to clear it. */
  private readonly shieldForward: number;
  private shield: THREE.Object3D | null = null;
  private shieldId = 0;
  private shieldTone: Tone = "family";
  private shieldGeneration = -1;
  private held: THREE.Object3D | null = null;
  private readonly tint = new THREE.Color(1, 1, 1);
  private hurt = 0;
  private heldId = 0;
  private heldTone: Tone = "family";
  private heldGeneration = -1;
  /**
   * A rigged model in place of the drawn body, for a skin that has one -
   * see skinModels. The drawn parts are built all the same and hidden, so
   * that everything hung on them - the hand, the shield, the crouch - keeps
   * its numbers, and a body made before the model arrived is a drawing
   * until it does.
   */
  private readonly skinId: string;
  private rig: SkinModelInstance | null = null;
  private rigGeneration = -1;
  private mixer: THREE.AnimationMixer | null = null;
  private rigActions: { idle: THREE.AnimationAction | null; walk: THREE.AnimationAction | null; run: THREE.AnimationAction | null } =
    { idle: null, walk: null, run: null };
  private rigPlaying: THREE.AnimationAction | null = null;
  private rigTicked = 0;
  private drawn: THREE.Object3D[] = [];

  constructor(def: SkinDef) {
    const p = def.proportions;
    const layout = layoutSkin(p);
    this.skinId = def.id;
    /*
     * A child is a grown-up scaled down, and that is the only way one can be.
     *
     * Every skin is thirty-two pixels from sole to crown so that the camera
     * sits at one eye level whatever you choose to look like; a village child
     * has to be *shorter* than that, and no arrangement of proportions can
     * say so. This is the one number that can. The first-person arm below
     * does not read it: you never see your own child.
     */
    this.group.scale.setScalar(def.stature ?? 1);

    /*
     * A copy of the skin's material, not the skin's material.
     *
     * The texture inside it is still shared - that is the expensive part and
     * the reason the cache exists. The material is a handful of numbers, and
     * sharing it meant a colour set on one body was set on every body wearing
     * that skin: hitting one pig lit up the whole herd. Nobody noticed while
     * the only colour anyone set was the daylight, which is the same for all
     * of them.
     */
    this.material = skinMaterial(def).clone();
    /*
     * The outer layer's own material, cloned for the same reason.
     *
     * Tint is set per body, and a shared material means one мавка walking
     * into the shade takes every мавка with her.
     */
    this.outer = outerMaterial(def).clone();

    /** A part's second layer, hung on the same pivot as the part itself. */
    const over = (
      part: "head" | "torso" | "arm" | "leg", name: PartName,
    ): THREE.Mesh | null => {
      const size = overlayOf(p, part);
      const faces: BoxFaces | undefined = layout[name];
      if (size === undefined || faces === undefined) return null;
      return makePart(size, faces, this.outer);
    };

    const legTop = p.leg.h * PIXEL;
    const torsoTop = legTop + p.torso.h * PIXEL;

    this.legR = makeJoint(p.leg, layout.legR!, this.material, -(p.leg.w * PIXEL) / 2, legTop);
    this.legL = makeJoint(p.leg, layout.legL!, this.material, (p.leg.w * PIXEL) / 2, legTop);
    /*
     * An overlay goes on the limb's own pivot, hanging like the limb does.
     *
     * A skirt that did not swing with the leg under it would be a skirt
     * standing still while its owner walked out of it.
     */
    for (const [pivot, name] of [
      [this.legR, "legROver"], [this.legL, "legLOver"],
    ] as const) {
      const skirt = over("leg", name);
      if (skirt !== null) {
        // Hung from the hip like the leg, so the two swing together.
        skirt.position.y = -(overlayOf(p, "leg")!.h * PIXEL) / 2;
        pivot.add(skirt);
      }
    }

    const torso = makePart(p.torso, layout.torso!, this.material);
    torso.position.y = legTop + (p.torso.h * PIXEL) / 2;
    const robe = over("torso", "torsoOver");
    if (robe !== null) {
      // Level with the torso, and a shade lower: what hangs off shoulders
      // hangs *down*.
      robe.position.y = legTop + (p.torso.h * PIXEL) / 2
        - (overlayOf(p, "torso")!.h - p.torso.h) * PIXEL / 2;
    }

    /*
     * The right arm on the right.
     *
     * She faces -z, so her right is +x. The right arm was hung at -x for as
     * long as the model existed and nobody could tell, because both arms
     * are the same shape - until the sabre went into the right hand and
     * showed up, seen from the front, in the left. "На цьому скріні зброя
     * в правій руці, але якщо подивитись зі сторони, то зброя буде в
     * лівій." The first-person fist is on the right of the screen, and
     * this is the arm it belongs to.
     */
    const shoulderX = ((p.torso.w + p.arm.w) * PIXEL) / 2;
    this.armR = makeJoint(p.arm, layout.armR!, this.material, shoulderX, torsoTop);
    this.armL = makeJoint(p.arm, layout.armL!, this.material, -shoulderX, torsoTop);
    // Half the arm's depth - the sleeve's, where there is one - and a little
    // air: a shield set inside the arm shows as a splinter. It did, on every
    // skin with sleeves; the skeleton's bare bones were thin enough to miss it.
    this.shieldForward = ((overlayOf(p, "arm") ?? p.arm).d * PIXEL) / 2 + 0.06;
    for (const [pivot, name] of [
      [this.armR, "armROver"], [this.armL, "armLOver"],
    ] as const) {
      const sleeve = over("arm", name);
      if (sleeve !== null) {
        sleeve.position.y = -(overlayOf(p, "arm")!.h * PIXEL) / 2;
        pivot.add(sleeve);
      }
    }

    // Head and hat share a pivot at the neck so they pitch together.
    this.neck = new THREE.Group();
    this.neck.position.y = torsoTop;
    const head = makePart(p.head, layout.head!, this.material);
    head.position.y = (p.head.h * PIXEL) / 2;
    this.neck.add(head);
    const hair = over("head", "headOver");
    if (hair !== null) {
      // On the neck with the head, so it turns when she looks at you.
      hair.position.y = (p.head.h * PIXEL) / 2;
      this.neck.add(hair);
    }
    if (p.hat !== undefined) {
      const hat = makePart(p.hat, layout.hat!, this.material);
      hat.position.y = (p.head.h + p.hat.h / 2) * PIXEL;
      this.neck.add(hat);
    }

    /*
     * The hand is a child of the arm, not of the body.
     *
     * So the axe swings when the arm does, and points where the arm points,
     * without anything having to work out where the hand ended up this frame.
     */
    this.hand.position.y = -p.arm.h * PIXEL;
    // And the off hand, for a shield. Same trick, other arm: it hangs where
    // that arm hangs and swings with it.
    this.offHand.position.y = -p.arm.h * PIXEL * 0.72;
    this.placeHands();

    this.group.add(this.legR, this.legL, torso, this.armR, this.armL, this.neck);
    if (robe !== null) this.group.add(robe);
    // Everything above the knees, so a crouch can drop it in one move.
    this.above = [torso, this.armR, this.armL, this.neck,
      ...(robe === null ? [] : [robe])];
    this.standing = this.above.map((part) => part.position.y);
    this.drawn = [this.legR, this.legL, torso, this.armR, this.armL, this.neck,
      ...(robe === null ? [] : [robe])];
    this.adoptRig();
  }

  /** Take the skin's model, if it has one and it has arrived. */
  private adoptRig(): void {
    if (this.rig !== null || !hasSkinModel(this.skinId)) return;
    if (this.rigGeneration === skinModelGeneration()) return;
    this.rigGeneration = skinModelGeneration();
    const rig = skinModel(this.skinId);
    if (rig === null) return;
    this.rig = rig;
    for (const part of this.drawn) part.visible = false;
    // Stood as tall as a drawn body, feet where its feet are, and turned
    // about: a glTF character faces +z, a drawn body here faces -z.
    rig.root.scale.setScalar(PLAYER_HEIGHT / rig.height);
    rig.root.rotation.y = Math.PI;
    this.group.add(rig.root);
    const mixer = new THREE.AnimationMixer(rig.root);
    this.mixer = mixer;
    const clip = (name: string): THREE.AnimationAction | null => {
      const found = THREE.AnimationClip.findByName(rig.clips, name) ?? rig.clips[0];
      return found === undefined ? null : mixer.clipAction(found);
    };
    this.rigActions = {
      idle: clip(rig.spec.clips.idle), walk: clip(rig.spec.clips.walk), run: clip(rig.spec.clips.run),
    };
    this.rigTicked = performance.now();
    this.play(this.rigActions.idle);
    /*
     * Feet on the floor. The height came from the pose the file was bound
     * in; the clip she stands in sits her lower, and she went five
     * centimetres into the ground. Measured once, posed, and lifted.
     */
    mixer.update(0);
    this.group.updateMatrixWorld(true);
    const floor = new THREE.Box3();
    const toGroup = this.group.matrixWorld.clone().invert();
    rig.root.traverse((o) => {
      if (!(o instanceof THREE.SkinnedMesh)) return;
      o.computeBoundingBox();
      if (o.boundingBox === null) return;
      floor.union(o.boundingBox.clone().applyMatrix4(o.matrixWorld).applyMatrix4(toGroup));
    });
    if (!floor.isEmpty()) rig.root.position.y -= floor.min.y;
    this.placeHands();
    this.applyColour();
    this.setCrouch(this.crouched);
  }

  private play(action: THREE.AnimationAction | null): void {
    if (action === null || action === this.rigPlaying) return;
    action.reset().play();
    if (this.rigPlaying !== null) this.rigPlaying.crossFadeTo(action, 0.2, false);
    this.rigPlaying = action;
  }

  /**
   * Put something in the hand, or nothing.
   *
   * Rebuilt only when it changes: this is called every frame, and a new cube
   * and a new material sixty times a second would be a leak with a view.
   */
  setHeld(id: number, tone: Tone = "family"): void {
    // Or the same thing whose model has since arrived: then it is drawn again.
    if (id === this.heldId && tone === this.heldTone && this.heldGeneration === heldModelGeneration()) return;
    this.heldId = id;
    this.heldTone = tone;
    this.heldGeneration = heldModelGeneration();
    if (this.held !== null) {
      this.hand.remove(this.held);
      disposeHeldMesh(this.held);
      this.held = null;
    }
    const mesh = makeHeldMesh(id, tone);
    if (mesh === null) return;
    mesh.scale.setScalar(id < FIRST_NON_BLOCK_ITEM
      ? HELD_SCALE
      : HELD_ITEM_SCALE * heldStyle(id).scale);
    // Held out in front of the fist and tipped, so a blade reads as a blade
    // rather than as a card standing edge-on to whoever is looking.
    // In the fist rather than out in front of it: the first version held it
    // far enough forward that it read as floating beside the hand.
    mesh.position.set(0, -HELD_SCALE * 0.10, -HELD_SCALE * 0.22);
    /*
     * Pointing the way its owner is facing.
     *
     * A quarter turn about x sends the drawing's "up" to the body's forward,
     * so the blade lies out in front of the fist instead of standing over the
     * shoulder like a rifle at ease. The rest lifts the point a little, which
     * is how a man carries a sabre he is not yet swinging.
     */
    /*
     * A blade sweeps forward and up from the fist; a shafted tool hangs
     * down and forward, head towards the ground, the way a pick is
     * carried - "ось приклад вигляду зі сторони, коли в руці кайло". On
     * the picture's face, up is forward and right is up, so the tool's
     * native head-direction (down-right) is turned half round.
     */
    const style = heldStyle(id);
    if (style.shafted) {
      /*
       * A shafted tool hangs from the fist, down and forward, head towards
       * the ground - "ось приклад вигляду зі сторони, коли в руці кайло".
       * Its picture stands sideways to the body, the way a carried pick
       * is seen from beside its carrier, and is turned in its own plane so
       * the handle runs down at half a right angle from the hand.
       */
      mesh.rotation.set(0.15, Math.PI / 2, 0.7 + Math.PI);
    } else if (drawnFlat(id)) {
      // A blade sweeps forward and up from the fist, its picture standing
      // sideways to the body so its flat is seen from beside its carrier -
      // "розверни на 90 для вигляду зі сторони" - not its edge.
      mesh.rotation.set(0.25 - style.tilt, Math.PI / 2, 0.35 + style.turn);
      /*
       * "Орк шаблю тримає гострою стороною до себе. Позиція правильна,
       * просто треба розвернути." A modelled sabre is curved, and its edge
       * is on the outside of the curve; laid the drawing's way it came out
       * facing its owner. Half a turn about the blade itself - the
       * drawing's diagonal, hilt bottom-left to tip top-right - keeps hilt
       * and tip where they were and puts the edge outward.
       */
      if (hasHeldModel(id)) mesh.quaternion.multiply(BLADE_FLIP);
    } else {
      // A block: held out in front of the fist and tipped, so a stair reads
      // as a stair from the front and not as a cube of its material.
      mesh.rotation.set(-Math.PI / 2 + 0.25 - style.tilt, 0, 0.35 + style.turn);
    }
    this.hand.add(mesh);
    this.held = mesh;
  }

  /**
   * A shield on the off arm, or nothing.
   *
   * Its own thing rather than a second `setHeld`, because a shield is not
   * held the way a sabre is: a blade points where the arm points and a
   * shield is strapped flat across the forearm, facing whatever is in front
   * of its owner. So it is turned a quarter about the arm and stood upright,
   * which is what puts its face outward instead of edge-on.
   */
  setShield(id: number, tone: Tone = "family"): void {
    if (id === this.shieldId && tone === this.shieldTone && this.shieldGeneration === heldModelGeneration()) return;
    this.shieldId = id;
    this.shieldTone = tone;
    this.shieldGeneration = heldModelGeneration();
    if (this.shield !== null) {
      this.offHand.remove(this.shield);
      disposeHeldMesh(this.shield);
      this.shield = null;
    }
    const mesh = makeHeldMesh(id, tone);
    if (mesh === null) return;
    // Big enough to be a shield and no bigger: at a third again it covered
    // half of him and stood out past his shoulder like a door.
    mesh.scale.setScalar(HELD_ITEM_SCALE * heldStyle(id).scale * 0.95);
    /*
     * Out from the arm and forward of it, covering the chest.
     *
     * Level with the forearm rather than the fist - a shield is strapped
     * along the arm, and hung from the hand it swings about at the hip like
     * a bucket. Forward, because what it is for is being between its owner
     * and whatever is in front of them.
     */
    mesh.position.set(-HELD_SCALE * 0.02, HELD_SCALE * 0.30, -this.shieldForward);
    /*
     * No turn at all, which took two goes to find out.
     *
     * An item sprite is extruded standing in the plane of the picture, so
     * its face already points the way its owner does. Turning it a quarter
     * about the upright axis - the obvious fix when the first attempt showed
     * a grey splinter beside the arm - turns that face *away*, and what
     * comes out is a plank seen edge-on with the boards reading as the rungs
     * of a ladder. The splinter was never the rotation: it was the shield
     * sitting inside the arm with only its edge clear of it.
     */
    mesh.rotation.set(0, 0, 0);
    this.offHand.add(mesh);
    this.shield = mesh;
  }

  /** Tint the body to match the world's light level. */
  setTint(color: THREE.Color): void {
    this.tint.copy(color);
    this.applyColour();
  }

  /**
   * Show that a blow just landed, 1 down to 0.
   *
   * The one thing you could not see before: a swing connected, the health
   * went down and the animal carried on grazing exactly as it had been. A
   * hit that leaves no mark is a hit you have to take on trust.
   */
  setHurt(glow: number): void {
    if (glow === this.hurt) return;
    this.hurt = glow;
    this.applyColour();
  }

  private applyColour(): void {
    this.material.color.copy(this.tint).lerp(HURT_COLOUR, this.hurt);
    // The second layer darkens and flashes with the first, or a мавка in the
    // shade is a dark girl in a bright dress.
    this.outer.color.copy(this.material.color);
    // And the model, where there is one: it is one body.
    if (this.rig !== null) for (const m of this.rig.materials) m.color.copy(this.material.color);
  }

  /**
   * Pose the body for this frame.
   *
   * `swingPhase` advances with distance travelled rather than with time, so
   * the stride matches the feet instead of sliding.
   */
  /**
   * На́вприсядки: knees out, everything above them dropped.
   *
   * Not a scale of the whole body - that gives a doll of a person rather
   * than a person crouching. The legs bend at the hip, and the torso, arms
   * and head come down by the height the body lost, which is the difference
   * between standing and crouching in the controller.
   */
  setCrouch(on: boolean): void {
    this.crouched = on;
    const drop = on ? PLAYER_HEIGHT - CROUCH_HEIGHT : 0;
    this.above.forEach((part, i) => {
      part.position.y = (this.standing[i] ?? 0) - drop;
    });
    // A model has no knees to bend yet: it ducks by being shorter.
    if (this.rig !== null) {
      const tall = PLAYER_HEIGHT / this.rig.height;
      this.rig.root.scale.set(tall, tall * (on ? CROUCH_HEIGHT / PLAYER_HEIGHT : 1), tall);
    }
  }

  setPose(
    yaw: number, pitch: number, swingPhase: number, swingAmount: number,
    strike = 0,
  ): void {
    this.group.rotation.y = yaw;
    if (this.rig === null) this.adoptRig();
    if (this.rig !== null && this.mixer !== null) {
      // The clip does the walking: standing still or on the move, and the
      // mixer runs on the clock since the pose is set once a frame.
      this.play(swingAmount > 0 ? this.rigActions.walk : this.rigActions.idle);
      const now = performance.now();
      this.mixer.update(Math.min(0.1, (now - this.rigTicked) / 1000));
      this.rigTicked = now;
      // The head follows the look and the arm the blow, on top of the clip.
      this.rig.head?.rotateX(-pitch);
      if (strike > 0) this.rig.arm?.rotateX(-strikeAngle(strike));
      return;
    }

    // A crouched stride is a shuffle: the legs are already bent and they
    // move a fraction as far.
    const swing = Math.sin(swingPhase) * (this.crouched ? 0.3 : 0.95) * swingAmount;
    this.legR.rotation.x = swing + (this.crouched ? 0.55 : 0);
    this.legL.rotation.x = -swing + (this.crouched ? 0.55 : 0);
    // Arms counter-swing against the legs, as they do when walking.
    this.armR.rotation.x = -swing;
    this.armL.rotation.x = swing;

    /*
     * A blow overrides the stride in the striking arm.
     *
     * Added to the walk swing rather than replacing it and the arm would sag
     * or snap depending on which foot was down when you clicked - the blow
     * has to look the same whether you are standing still or running.
     */
    if (strike > 0) this.weaponArm().rotation.x = -strikeAngle(strike);

    this.neck.rotation.x = -pitch;
  }

  private weaponArm(): THREE.Group {
    return this.handedness === "left" ? this.armL : this.armR;
  }

  /**
   * Put the weapon in the other hand, and the shield in the one it left.
   *
   * The hand groups move from arm to arm with whatever is in them, so the
   * held thing, the shield and the blow all follow without being told.
   */
  setHand(hand: Hand): void {
    if (hand === this.handedness) return;
    this.handedness = hand;
    this.placeHands();
  }

  /**
   * Hang the hands off the arms the handedness says.
   *
   * Each hand is mirrored when it is on the far side of the body from
   * where its look was found: the sabre's turn was tuned with the arm at
   * -x and sweeps across the hip from there, the shield's with the arm at
   * +x. Moved unmirrored, the blade pointed out sideways like a signpost.
   */
  private placeHands(): void {
    if (this.rig !== null) {
      // On the model's hands. A bone's y runs from wrist to fingers, the
      // drawn arm's from hand to shoulder: half a turn puts a held thing
      // the way the drawing holds it.
      const left = this.handedness === "left";
      const weaponBone = left ? this.rig.offHand : this.rig.hand;
      const offBone = left ? this.rig.hand : this.rig.offHand;
      // A bone may be scaled - a Mixamo rig comes in centimetres - and a
      // thing hung on it would shrink with it; hung at the size it would
      // have on a drawn arm instead.
      this.group.updateMatrixWorld(true);
      const own = this.group.getWorldScale(new THREE.Vector3());
      const sizeOn = (bone: THREE.Object3D, mirror: boolean): THREE.Vector3 => {
        const at = bone.getWorldScale(new THREE.Vector3());
        return new THREE.Vector3((mirror ? -1 : 1) * own.x / at.x, own.y / at.y, own.z / at.z);
      };
      this.hand.removeFromParent();
      if (weaponBone !== null) {
        weaponBone.add(this.hand);
        this.hand.position.set(0, 0, 0);
        this.hand.rotation.set(Math.PI, 0, 0);
        this.hand.scale.copy(sizeOn(weaponBone, left));
      }
      this.offHand.removeFromParent();
      if (offBone !== null) {
        offBone.add(this.offHand);
        this.offHand.position.set(0, 0, 0);
        this.offHand.rotation.set(Math.PI, 0, 0);
        this.offHand.scale.copy(sizeOn(offBone, !left));
      }
      return;
    }
    const weapon = this.weaponArm();
    const off = weapon === this.armR ? this.armL : this.armR;
    this.hand.removeFromParent();
    weapon.add(this.hand);
    this.hand.scale.x = weapon.position.x > 0 ? -1 : 1;
    this.offHand.removeFromParent();
    off.add(this.offHand);
    this.offHand.scale.x = off.position.x < 0 ? -1 : 1;
  }

  /**
   * Give back what this body owns: its geometry and its own material.
   *
   * The texture inside the material is shared with every other body wearing
   * the same skin and is left alone - disposing that would blank every orc on
   * screen the first time one died. The material itself is this body's copy
   * and would otherwise leak, one per animal that ever walked past.
   */
  dispose(): void {
    this.material.dispose();
    if (this.held !== null) {
      this.hand.remove(this.held);
      disposeHeldMesh(this.held);
      this.held = null;
    }
    this.mixer?.stopAllAction();
    if (this.rig !== null) for (const m of this.rig.materials) m.dispose();
    this.group.traverse((o) => {
      // A model's geometry is shared between every body wearing it.
      if (o instanceof THREE.Mesh && o.userData.sharedGeometry !== true) o.geometry.dispose();
    });
  }
}

/**
 * Your own arm, in front of the camera.
 *
 * The thing that was missing. A held item was drawn hanging in the air at
 * the bottom right of the screen with nothing attached to it, so it read as
 * floating *beside* a hand rather than being *in* one - because there was no
 * hand. This is the same arm box the body wears, cut from the same skin, so
 * whichever козак you chose is the one holding the axe.
 *
 * Life size and close, which is how a first-person arm is always done: the
 * alternative is a small arm further away, and that looks like somebody
 * else's arm across the room.
 */
export class ViewArm {
  readonly group = new THREE.Group();

  /**
   * What the corner of the screen holds.
   *
   * An empty hand is the arm, fist up, as it always was. With something in
   * it the arm goes and the thing itself rises out of the bottom-right
   * corner, big, its handle end at the corner and its business end towards
   * the middle of the picture - "щоб руки видно не було, а шабля та
   * інструменти тримались в руці, як показано; якщо в руці нічого немає,
   * нехай вона залишається видимою, як зараз". The shield fills the
   * bottom-left. The shoulder is the group that moves on a blow.
   */
  private readonly material: THREE.MeshBasicMaterial;
  private readonly arm: THREE.Mesh;
  private readonly shoulder = new THREE.Group();
  private readonly hand = new THREE.Group();
  private held: THREE.Object3D | null = null;
  private heldId = -1;
  private heldTone: Tone = "family";
  private heldGeneration = -1;
  /** Whether what is held is a shafted tool, which strikes differently. */
  private heldShafted = false;
  /** The other corner of the screen, where the shield shows. */
  private readonly offHand = new THREE.Group();
  private shield: THREE.Object3D | null = null;
  private shieldId = -1;
  private shieldTone: Tone = "family";
  private shieldGeneration = -1;

  constructor(def: SkinDef) {
    const p = def.proportions;
    const layout = layoutSkin(p);
    this.material = skinMaterial(def).clone();
    /*
     * The arm, for when the hand is empty. Placed, not hung: the fist is
     * put where it should be on screen and the forearm laid so that its
     * fist end arrives there. Camera units throughout - x to the right, y
     * up, z towards the eye - so the numbers mean something you can look at.
     */
    this.arm = makePart(p.arm, layout.armR!, this.material);
    this.arm.scale.setScalar(ARM_SCALE);
    this.arm.position.set(0.52, -0.62, -0.95);
    this.arm.rotation.set(0.10, 0, 0.55 + Math.PI);
    this.shoulder.add(this.arm);

    // The hand sits a little inside the corner so a handle end is just off
    // the screen, the way a hand would be.
    this.hand.position.set(0.58, -0.58, -0.90);
    this.shoulder.add(this.hand);
    this.offHand.position.set(-0.56, -0.56, -0.90);
    this.group.add(this.offHand);
    this.group.add(this.shoulder);
  }

  setHeld(id: number, tone: Tone): void {
    // Or the same thing whose model has since arrived: then it is drawn again.
    if (id === this.heldId && tone === this.heldTone && this.heldGeneration === heldModelGeneration()) return;
    this.heldId = id;
    this.heldTone = tone;
    this.heldGeneration = heldModelGeneration();
    if (this.held !== null) {
      this.hand.remove(this.held);
      disposeHeldMesh(this.held);
      this.held = null;
    }
    const mesh = makeHeldMesh(id, tone);
    // The arm shows only when there is nothing in the hand.
    this.arm.visible = mesh === null;
    if (mesh === null) return;
    const style = heldStyle(id);
    const flat = drawnFlat(id);
    this.heldShafted = flat && style.shafted;
    this.hand.rotation.set(0, 0, 0);
    // A shafted tool a little bigger, since turned edge-on its head is
    // foreshortened - and thinner through the picture, a third and then
    // half again ("зроби його тоншим на 30%", "ще на 50% тоншою"): the
    // two-pixel extrusion reads as a slab at this angle.
    // A blade bigger still: it is the nearest thing to the eye and half of
    // it is off the screen, like a sword carried in the corner of the eye.
    const size = (flat ? VIEW_HELD_SCALE : VIEW_BLOCK_SCALE) * style.scale *
      (style.shafted ? 1.1 : flat ? 1.35 : 1);
    mesh.scale.set(size, size, size * (style.shafted ? 0.35 : 1));
    if (flat) {
      /*
       * Gripped at the corner, pointing up and in.
       *
       * The drawing's grip point is the mesh's origin, so the hand's
       * position is where the handle is held. A third of a turn about the
       * face of the picture leans the blade towards the middle of the
       * screen; a little about the upright shows its flat rather than its
       * edge. A shafted tool carries its own quarter turn in `turn`.
       */
      /*
       * A blade is drawn hilt bottom-left, tip top-right; a shafted tool
       * head up, handle down. Each is turned about the face of the picture
       * so its business end leans in towards the middle of the screen from
       * the corner.
       *
       * The tool is also turned most of the way about the upright, so one
       * point of the pick looks ahead and the other back at you - "розверни
       * по осі Y, щоб гострий край дивився вперед, а другий назад" - and it
       * sits out at the very edge of the screen, since no hand is drawn.
       */
      // Out at the right edge of the screen: "посунь кайло до правого краю".
      /*
       * A shafted tool: lower, so the handle end meets the bottom of the
       * screen, and turned about the upright, counter-clockwise from above.
       * A blade: out of the very corner, its hilt off the screen and only
       * the blade in view, rising up and in, and turned about the upright
       * so it is seen at an angle rather than flat - "розверни його як
       * тут, там його навіть видно не повністю".
       */
      /*
       * A blade at the very edge, turned to face the eye.
       *
       * Out at the edge it is seen well off the line of sight, so a blade
       * standing square to the screen shows its side along its inner edge -
       * a second row of blocks. Turned clockwise about the upright by about
       * what the eye is off, the side goes: "трохи розвернути, щоб не було
       * видно другого ряду, по Y десь на 5° за годинниковою".
       */
      mesh.position.set(style.shafted ? 0.30 : 0.52, style.shafted ? -0.04 : -0.36, 0);
      mesh.rotation.set(0.10 - style.tilt, style.shafted ? 1.2 : -0.45,
        style.shafted ? 3.55 - Math.PI : 1.15 + style.turn);
    } else {
      // A block: a cube sitting in the corner, turned so three faces show.
      mesh.position.set(0.02, 0.06, 0);
      mesh.rotation.set(0.35, -0.6, 0);
    }
    this.hand.add(mesh);
    this.held = mesh;
  }

  /**
   * The shield, filling the other corner, or nothing.
   *
   * Big and nearly face-on: it is at arm's length and it is what is between
   * you and whatever is in front of you.
   */
  setShield(id: number, tone: Tone): void {
    if (id === this.shieldId && tone === this.shieldTone && this.shieldGeneration === heldModelGeneration()) return;
    this.shieldId = id;
    this.shieldTone = tone;
    this.shieldGeneration = heldModelGeneration();
    if (this.shield !== null) {
      this.offHand.remove(this.shield);
      disposeHeldMesh(this.shield);
      this.shield = null;
    }
    const mesh = makeHeldMesh(id, tone);
    if (mesh === null) return;
    mesh.scale.setScalar(VIEW_HELD_SCALE * heldStyle(id).scale * 1.5);
    mesh.rotation.set(0.10, 0.40, 0.05);
    mesh.position.y = -VIEW_SHIELD_DROP;
    this.offHand.add(mesh);
    this.shield = mesh;
  }

  /**
   * Which side of the screen the hand is on.
   *
   * A mirror, not a second layout: every number above was found by
   * looking, and the left-handed picture is the same picture in a glass.
   * Three.js turns the winding round for a negative scale on its own.
   */
  setHand(hand: Hand): void {
    this.group.scale.x = hand === "left" ? -1 : 1;
  }

  /**
   * Swing what is held.
   *
   * Slid and turned about the grip: the hand drops and comes forward and
   * the thing in it tips over towards the middle of the screen, which is
   * what you can see of your own blow. Every number is a distance in the
   * picture rather than an angle at a joint you cannot see.
   */
  setStrike(progress: number): void {
    const t = strikeAngle(progress) / STRIKE_REACH;
    if (this.heldShafted) {
      /*
       * A pick chops: it pivots at the fist and its head swings forward
       * and down, into the picture, and comes back. Nothing about the
       * middle of the screen - "відв'яжемось від центру екрану, нехай
       * просто махає вперед". The fist itself moves a little forward
       * and down with the swing, as a fist does.
       */
      this.shoulder.position.set(0, -0.06 * t, -0.10 * t);
      this.hand.rotation.set(-1.2 * t, 0, 0);
      return;
    }
    this.shoulder.position.set(-0.10 * t, -0.12 * t, 0.05 * t);
    this.hand.rotation.set(-0.9 * t, 0, 0.5 * t);
  }

  setTint(color: THREE.Color): void {
    this.material.color.copy(color);
  }

  dispose(): void {
    if (this.held !== null) {
      this.hand.remove(this.held);
      disposeHeldMesh(this.held);
    }
    if (this.shield !== null) {
      this.offHand.remove(this.shield);
      disposeHeldMesh(this.shield);
    }
    this.material.dispose();
    this.arm.geometry.dispose();
  }
}

/** How big a held thing is in your own hand. Life size, like the arm. */
/**
 * How big a drawn item is in your own hand.
 *
 * Halved when the blade was turned to point at the crosshair: pointing away
 * it was seen along its own length and foreshortened to nothing, so the
 * number that made it a dagger then made it fill half the screen once it was
 * turned round. The same size, seen properly.
 */
const VIEW_HELD_SCALE = 0.62;
/**
 * How far the shield sits below the corner it used to fill: "щит стало
 * видно занадто багато, опусти на 80% вниз" - a fifth of it in sight.
 * Measured on the stage: 58,995 shield pixels at 0, 11,944 at 0.6.
 */
let VIEW_SHIELD_DROP = 0.6;
/** For the stage, which measures how much of the shield shows. */
export function setViewShieldDrop(d: number): void { VIEW_SHIELD_DROP = d; }
/**
 * A block is smaller than a weapon.
 *
 * A cube fills its whole square; a шабля is a line across one, so the same
 * number makes the sword look like a toy beside the brick. Two numbers.
 */
const VIEW_BLOCK_SCALE = 0.42;

/**
 * How big your own arm is drawn, against the body's.
 *
 * The arm and not the group: scaling the whole thing scaled the shoulder's
 * position too, so shrinking the arm walked it towards the middle of the
 * screen. A life-sized arm at arm's length from the eye is a quarter of the
 * screen, which is what the first attempt looked like.
 */
const ARM_SCALE = 0.62;

